<template>
  <div id="exclusive-content" class="p-3">
    <h2 class="title w-full pb-2">{{ $t("exclusive_content") }}</h2>

    <div class="exclusive-content-filters flex flex-wrap mb-3">
      <Dropdown
        v-model="filters.rubrica"
        :options="taxonomyOptions.rubrica"
        option-label="name"
        option-value="slug"
        :placeholder="$t('filter_rubrica')"
        show-clear
        class="w-12rem exclusive-content-filter"
      />
      <Dropdown
        v-model="filters.tema"
        :options="taxonomyOptions.tema"
        option-label="name"
        option-value="slug"
        :placeholder="$t('filter_tema')"
        show-clear
        class="w-12rem exclusive-content-filter"
      />
      <Dropdown
        v-model="filters.pais"
        :options="taxonomyOptions.pais"
        option-label="name"
        option-value="slug"
        :placeholder="$t('filter_pais')"
        show-clear
        class="w-12rem exclusive-content-filter"
      />
      <Dropdown
        v-model="filters.tipo_conteudo"
        :options="taxonomyOptions.tipo_conteudo"
        option-label="name"
        option-value="slug"
        :placeholder="$t('filter_tipo')"
        show-clear
        class="w-12rem exclusive-content-filter"
      />
      <Button
        :class="{ 'p-button-outlined': !filters.featured }"
        class="exclusive-content-filter"
        :label="$t('filter_featured')"
        icon="pi pi-star"
        @click="filters.featured = !filters.featured"
      />
    </div>

    <ProgressBar v-if="isLoading" mode="indeterminate" class="text-center" />

    <div v-else class="grid">
      <div
        v-for="article in filteredArticles"
        :key="article.id"
        class="col-12 md:col-6 lg:col-4"
      >
        <div
          class="
            article-card
            surface-card
            border-round
            p-3
            h-full
            flex flex-column
          "
        >
          <div v-if="featuredImage(article)" class="article-card-image mb-2">
            <img
              :src="featuredImage(article)"
              :alt="decodeTitle(article.title.rendered)"
              loading="lazy"
            />
          </div>
          <span
            v-if="visualLabel(article)"
            class="text-xs font-bold text-primary mb-1"
          >
            {{ visualLabel(article) }}
          </span>
          <h4 class="article-card-title m-0 mb-2">
            {{ decodeTitle(article.title.rendered) }}
          </h4>
          <div class="article-card-tags flex flex-wrap mb-2">
            <span
              v-for="tax in articleTaxonomyBadges(article)"
              :key="tax.slug"
              class="
                article-card-tag
                text-xs
                surface-200
                border-round
                px-2
                py-1
              "
            >
              {{ tax.name }}
            </span>
          </div>
          <span
            v-if="hasExcerpt(article)"
            class="article-card-excerpt text-sm mb-3"
            v-html="$sanitize(article.excerpt.rendered)"
          />
          <div class="mt-auto flex align-items-center justify-content-between">
            <small v-if="readingTime(article)" class="text-color-secondary">
              {{ readingTime(article) }} {{ $t("reading_time") }}
            </small>
            <Button
              :label="$t('read_article')"
              icon="pi pi-arrow-right"
              :loading="openingId === article.id"
              @click="openArticle(article)"
            />
          </div>
        </div>
      </div>
      <div
        v-if="!filteredArticles.length"
        class="col-12 text-center text-color-secondary"
      >
        {{ $t("no_articles") }}
      </div>
    </div>
  </div>
</template>

<script>
export default {
  data() {
    return {
      articles: [],
      isLoading: true,
      openingId: null,
      filters: {
        rubrica: null,
        tema: null,
        pais: null,
        tipo_conteudo: null,
        featured: false,
      },
    };
  },
  computed: {
    filteredArticles() {
      return this.articles.filter((a) => {
        const matchTax = (taxonomy, slug) =>
          !slug || this.articleTerms(a, taxonomy).some((t) => t.slug === slug);
        if (!matchTax("rubrica", this.filters.rubrica)) return false;
        if (!matchTax("tema", this.filters.tema)) return false;
        if (!matchTax("pais", this.filters.pais)) return false;
        if (!matchTax("tipo_conteudo", this.filters.tipo_conteudo))
          return false;
        if (this.filters.featured && !(a.acf && a.acf.destaque_area_cliente))
          return false;
        return true;
      });
    },
    taxonomyOptions() {
      const collect = (taxonomy) => {
        const map = new Map();
        this.articles.forEach((a) =>
          this.articleTerms(a, taxonomy).forEach((t) => map.set(t.slug, t))
        );
        return Array.from(map.values());
      };
      return {
        rubrica: collect("rubrica"),
        tema: collect("tema"),
        pais: collect("pais"),
        tipo_conteudo: collect("tipo_conteudo"),
      };
    },
  },
  created() {
    this.getArticles();
  },
  methods: {
    getArticles() {
      this.$root
        .apiWordpressBEGet(this.$root.api.externalApiBalcaoMaisArticles)
        .then((response) => {
          this.articles = response.data || [];
          this.isLoading = false;
        })
        .catch(() => {
          this.isLoading = false;
          this.$root.toastError("error_loading_articles");
        });
    },
    articleTerms(article, taxonomy) {
      const groups = (article._embedded && article._embedded["wp:term"]) || [];
      const flat = groups.flat ? groups.flat() : [].concat.apply([], groups);
      return flat.filter((t) => t && t.taxonomy === taxonomy);
    },
    articleTaxonomyBadges(article) {
      return ["rubrica", "tema", "pais", "tipo_conteudo"]
        .map((tax) => this.articleTerms(article, tax))
        .reduce((acc, arr) => acc.concat(arr), [])
        .slice(0, 4);
    },
    featuredImage(article) {
      const media = article._embedded && article._embedded["wp:featuredmedia"];
      const item = media && media[0];
      if (!item || item.code === "rest_forbidden" || !item.source_url) {
        return null;
      }
      const sizes = item.media_details && item.media_details.sizes;
      if (sizes) {
        return (
          (sizes.large && sizes.large.source_url) ||
          (sizes.medium_large && sizes.medium_large.source_url) ||
          (sizes.medium && sizes.medium.source_url) ||
          item.source_url
        );
      }
      return item.source_url;
    },
    hasExcerpt(article) {
      const html = (article.excerpt && article.excerpt.rendered) || "";
      return (
        html
          .replace(/<[^>]+>/g, "")
          .replace(/&nbsp;/g, " ")
          .trim().length > 0
      );
    },
    visualLabel(article) {
      return article.acf && article.acf.etiqueta_visual;
    },
    readingTime(article) {
      return article.acf && article.acf.tempo_leitura;
    },
    decodeTitle(title) {
      return (title || "").replace(/(&#(\d+);)/g, (m, c, code) =>
        String.fromCharCode(code)
      );
    },
    openArticle(article) {
      this.openingId = article.id;
      this.$root
        .apiPost(this.$root.api.balcaoMaisContentToken, {
          articleId: article.id,
          articleUrl: article.link,
        })
        .then(({ data }) => {
          window.open(data.redirectUrl || article.link, "_blank", "noopener");
        })
        .catch(() => this.$root.toastError("error_generating_token"))
        .finally(() => {
          this.openingId = null;
        });
    },
  },
};
</script>

<style scoped>
.exclusive-content-filter {
  margin-right: 0.75rem;
  margin-bottom: 0.5rem;
}

.article-card-tag {
  margin-right: 0.5rem;
  margin-bottom: 0.35rem;
}

.article-card-image {
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 9;
  overflow: hidden;
  border-radius: 8px;
  background: var(--surface-200, #eef1f6);
}

.article-card-image img {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.article-card-title {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  line-height: 1.35;
  min-height: 2.7em;
}

.article-card-excerpt {
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>
